/*
 * Der Kern des HMI-Editors: reine Funktionen auf einem HmiProject-Entwurf.
 *
 * Hier steht kein Preact, kein DOM und kein Netzwerk. Der Grund ist nicht
 * Geschmack, sondern Pruefbarkeit: die Regeln, die ein HMI-Projekt konsistent
 * halten (kein verwaistes Placement, keine doppelte Id, kein Widget ausserhalb
 * eines festen Panels), sind genau die Regeln, die der Server beim Speichern
 * erneut prueft. Zwei Prueforte, die auseinanderlaufen, sind schlimmer als
 * einer, deshalb liegen die Regeln in einer Datei, die ein Test ohne Bundler
 * und ohne Browser fahren kann (siehe tests/editorModel.test.ts).
 *
 * ZWEI TRUTHS WAEREN EINER ZU VIEL. Die Interaktion im Browser laeuft ueber
 * Gridstack (dieselbe Bibliothek wie beim Dashboard), aber gespeichert wird
 * ausschliesslich das portable Schema aus `widgets.ts`:
 * `ScreenLayout.placements` mit col/row/colSpan/rowSpan je LayoutTarget.
 * Gridstack-eigener Zustand wird NIE persistiert. Das ist keine Vorsicht,
 * sondern Notwendigkeit: dasselbe HmiProject rendert auch die Kotlin-Panel-App,
 * und die kennt kein Gridstack. Ein Projekt, dessen Anordnung nur im
 * Engine-Format existiert, waere auf dem Panel leer.
 *
 * KOORDINATEN. `col` und `row` sind zero-based, wie Gridstacks x/y. Das
 * Schema schreibt nichts anderes vor, und eine Umrechnung an der Schnittstelle
 * waere eine Fehlerquelle ohne Gegenwert.
 */

import type {
  HmiProject,
  HmiScreen,
  LayoutTarget,
  ScreenBackground,
  ScreenLayout,
  WidgetDefinition,
  WidgetPlacement,
} from '../widgets';

// ---------------------------------------------------------------- Raster

/**
 * Rastermass eines Layout-Ziels.
 *
 * `rows` ist `null`, wo die Flaeche mitwaechst (Browser, scrollbar), und eine
 * Zahl, wo sie es nicht tut. Die beiden Panel-Ziele sind fest verbaute
 * Wandgeraete: dort gibt es kein Scrollen, und ein Widget unterhalb der
 * letzten Zeile ist nicht schwer zu finden, sondern nicht vorhanden. Der
 * Editor muss das verhindern koennen, und dafuer braucht er die Zahl.
 */
export interface GridSpec {
  columns: number;
  rows: number | null;
  /** Zeilenhoehe in px fuer die Darstellung im Editor. */
  cellHeightPx: number;
  /**
   * Breite der Zielflaeche in px, oder `null`, wo sie vom Fenster kommt.
   *
   * Nicht Kosmetik: J.8 verlangt eine Touch-Target-Pruefung, und
   * `docs/hmi-concept.md` nennt 64 px als Mindestmass fuer Handschuhbetrieb.
   * Beides ist nur zu beurteilen, wenn das Panel im Editor in seinem echten
   * Seitenverhaeltnis steht. Eine Flaeche, die in voller Fensterbreite
   * gezeigt wird, laesst jeden Knopf groesser aussehen, als er auf dem Geraet
   * ist, und das ist der Fehler, den man erst vor der Anlage bemerkt.
   */
  widthPx: number | null;
  /** Menschenlesbare Bezeichnung fuer die Ziel-Umschaltung. */
  label: string;
}

/**
 * Zwoelf Spalten ueberall ausser auf dem Handy.
 *
 * Absicht: eine colSpan von 4 heisst am PC und auf beiden Tablets dasselbe
 * Drittel. Wer ein Bild vom PC auf ein Tablet uebertraegt, muss dann nur die
 * Zeilen zusammenschieben und nicht jede Breite neu denken. Auf dem Handy
 * waeren zwoelf Spalten eine Luege, dort passt kein Widget in eine
 * Zwoelftelbreite.
 *
 * <b>Die Namen sind Geraeteklassen, die Token sind es nicht.</b> Was hier als
 * `label` steht, ist Beschriftung fuer Menschen: PC, Tablet, Handy. Die
 * Schluessel daneben (`desktopResponsive`, `panelFixed1024x600`, ...) sind der
 * Wire-Vertrag mit der Kotlin-Panel-App und stehen zeichengleich in
 * `HmiProject.cs` und in jedem gespeicherten Projekt. Sie werden NICHT
 * umbenannt, auch wenn "panelFixed" heute Tablet heisst: ein umbenanntes Token
 * macht jede gespeicherte Anordnung auf der halben Flotte unauffindbar, und
 * genau diese Sorte Drift hat dieses Repo schon zweimal getroffen.
 *
 * <b>Aufloesungen.</b> Die beiden Tablet-Ziele sind die Geraete, die ihr
 * ausliefert (7 Zoll mit 1024 auf 600, 10 Zoll mit 1280 auf 800), fest verbaut
 * und ohne Scrollbalken. Daraus kommen die Zeilenzahlen: 600 px bei 100 px
 * Zeilenhoehe sind sechs Zeilen, 800 px sind acht. Die 100 px sind gewaehlt,
 * nicht gemessen: ein Bedienelement, das ein Monteur mit Handschuh trifft,
 * braucht rund einen Zentimeter. Das Handy steht auf 390 px, der gaengigen
 * logischen Breite heutiger Geraete; der PC waechst mit dem Fenster mit, denn
 * dort gibt es keine eine richtige Aufloesung.
 */
export const GRID_SPECS: Readonly<Record<LayoutTarget, GridSpec>> = {
  desktopResponsive:
    { columns: 12, rows: null, cellHeightPx: 80, widthPx: null, label: 'PC' },
  panelFixed1024x600:
    { columns: 12, rows: 6, cellHeightPx: 100, widthPx: 1024, label: 'Tablet 7 Zoll' },
  panelFixed1280x800:
    { columns: 12, rows: 8, cellHeightPx: 100, widthPx: 1280, label: 'TP8/TP10 Pro quer' },
  panelPortrait800x1280:
    { columns: 8, rows: 12, cellHeightPx: 100, widthPx: 800, label: 'TP8/TP10 Pro hoch' },
  mobileResponsive:
    { columns: 4, rows: null, cellHeightPx: 80, widthPx: 390, label: 'Handy hoch' },
  mobileLandscape:
    { columns: 8, rows: 4, cellHeightPx: 90, widthPx: 844, label: 'Handy quer' },
};

/**
 * Entwurfsflaeche je Ziel fuer die freie Anordnung (docs/hmi-panel-ausbau.md
 * §1). Die Laufzeit skaliert sie gleichmaessig auf das Fenster. Der PC hat
 * keine eine richtige Groesse; 1440 x 900 ist ein gaengiger Laptop.
 */
export const FRAME_SIZES: Readonly<Record<LayoutTarget, { width: number; height: number }>> = {
  desktopResponsive: { width: 1440, height: 900 },
  panelFixed1024x600: { width: 1024, height: 600 },
  panelFixed1280x800: { width: 1280, height: 800 },
  panelPortrait800x1280: { width: 800, height: 1280 },
  mobileResponsive: { width: 390, height: 844 },
  mobileLandscape: { width: 844, height: 390 },
};

/**
 * Kleinste Kantenlaenge, die ein Bedienelement auf einem Panel haben soll.
 *
 * 64 px aus `docs/hmi-concept.md` §5: Handschuhbetrieb. Der Editor rechnet
 * damit nach, statt es dem Autor zu ueberlassen, es vor Ort zu merken. Gilt
 * nur fuer die festen Panel-Ziele; im Browser gibt es eine Maus.
 */
export const MIN_TOUCH_TARGET_PX = 64;

/**
 * Ist diese Platzierung auf diesem Ziel noch mit dem Finger zu treffen?
 *
 * `null` bedeutet "die Frage stellt sich hier nicht" (kein festes Panel).
 * Die Breite einer Zelle ergibt sich aus der Zielbreite geteilt durch die
 * Spaltenzahl; die Hoehe steht direkt im Rastermass.
 */
export function touchTargetSize(
  placement: WidgetPlacement,
  target: LayoutTarget,
): { widthPx: number; heightPx: number; ok: boolean } | null {
  const spec = GRID_SPECS[target];
  if (spec.rows === null || spec.widthPx === null) return null;

  const widthPx = (spec.widthPx / spec.columns) * placement.colSpan;
  const heightPx = spec.cellHeightPx * placement.rowSpan;
  return {
    widthPx,
    heightPx,
    ok: widthPx >= MIN_TOUCH_TARGET_PX && heightPx >= MIN_TOUCH_TARGET_PX,
  };
}

/** Reihenfolge der Ziele in der Oberflaeche: vom groessten zum kleinsten
 *  Geraet. Gezeichnet wird am PC, die uebrigen sind die Uebertragung, und in
 *  dieser Richtung wird auch uebernommen. */
export const LAYOUT_TARGET_ORDER: readonly LayoutTarget[] = [
  'desktopResponsive',
  'panelFixed1280x800',
  'panelPortrait800x1280',
  'panelFixed1024x600',
  'mobileLandscape',
  'mobileResponsive',
];

// ------------------------------------------------------------- Ids

/**
 * Id-Erzeugung fuer Widgets und Screens.
 *
 * Bewusst kein Guid: diese Ids stehen im gespeicherten JSON, das ein Mensch
 * liest, wenn etwas schiefgeht, und `gauge-7f3a` sagt beim Lesen mehr als
 * achtunddreissig Hexstellen. Eindeutigkeit wird nicht gehofft, sondern
 * geprueft, siehe {@link freshId}.
 */
const ID_ALPHABET = 'abcdefghijkmnopqrstuvwxyz023456789';

function randomSuffix(length: number): string {
  let out = '';
  const buf = new Uint8Array(length);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(buf);
  } else {
    for (let i = 0; i < length; i += 1) buf[i] = Math.floor(Math.random() * 256);
  }
  for (let i = 0; i < length; i += 1) out += ID_ALPHABET[buf[i] % ID_ALPHABET.length];
  return out;
}

/**
 * Neue Id mit Praefix, die in `taken` noch nicht vorkommt.
 *
 * Die Schleife ist kein Aberglaube: bei 34 hoch 4 Moeglichkeiten und einem
 * Projekt mit hunderten Widgets ist eine Kollision selten, aber nicht
 * unmoeglich, und eine doppelte Widget-Id waere ein Fehler, der sich erst
 * beim Rendern als verschwundenes Widget zeigt.
 */
export function freshId(prefix: string, taken: ReadonlySet<string>): string {
  for (let attempt = 0; attempt < 64; attempt += 1) {
    const candidate = `${prefix}-${randomSuffix(4)}`;
    if (!taken.has(candidate)) return candidate;
  }
  // Nach 64 Fehlschlaegen ist etwas anderes kaputt als der Zufall. Lieber
  // laenger und sicher als eine stille Dublette.
  return `${prefix}-${randomSuffix(12)}`;
}

/** Alle vergebenen Widget-Ids eines Projekts. */
export function widgetIds(project: HmiProject): Set<string> {
  return new Set(project.widgets.map((w) => w.id));
}

/** Alle vergebenen Screen-Ids eines Projekts. */
export function screenIds(project: HmiProject): Set<string> {
  return new Set(project.screens.map((s) => s.id));
}

// ------------------------------------------------------- Zugriffshelfer

/** Der Screen mit dieser Id, oder null. */
export function findScreen(project: HmiProject, screenId: string): HmiScreen | null {
  return project.screens.find((s) => s.id === screenId) ?? null;
}

/** Das Widget mit dieser Id, oder null. */
export function findWidget(project: HmiProject, id: string): WidgetDefinition | null {
  return project.widgets.find((w) => w.id === id) ?? null;
}

/**
 * Platzierungen eines Screens fuer ein Ziel.
 *
 * Fehlt der Layout-Eintrag, kommt ein leeres Objekt zurueck und nicht `null`.
 * Ein Screen ohne Layout fuer ein Ziel ist der Normalfall, solange niemand
 * dieses Ziel bearbeitet hat, und der Aufrufer soll dafuer keine Fallunter
 * scheidung brauchen. Das Rendern faellt dann auf Auto-Flow zurueck, so wie
 * HmiPlantPage es heute schon tut.
 */
export function placementsOf(
  project: HmiProject,
  screenId: string,
  target: LayoutTarget,
): Readonly<Record<string, WidgetPlacement>> {
  const screen = findScreen(project, screenId);
  const layout = screen?.layouts.find((l) => l.target === target);
  return layout?.placements ?? {};
}

// ------------------------------------------------- Aenderungen am Projekt
//
// Alle Operationen geben ein NEUES Projekt zurueck und lassen das
// uebergebene unveraendert. Das ist die Voraussetzung dafuer, dass
// Rueckgaengig und Wiederholen im Store ein Stapel aus Zustaenden sein
// koennen statt ein Stapel aus Gegenoperationen. Gegenoperationen muessten
// fuer jede Aktion einzeln richtig sein; ein Zustandsstapel ist es von
// selbst.

function withScreens(project: HmiProject, screens: HmiScreen[]): HmiProject {
  return { ...project, screens };
}

function mapScreen(
  project: HmiProject,
  screenId: string,
  fn: (screen: HmiScreen) => HmiScreen,
): HmiProject {
  return withScreens(
    project,
    project.screens.map((s) => (s.id === screenId ? fn(s) : s)),
  );
}

function upsertLayout(
  screen: HmiScreen,
  target: LayoutTarget,
  fn: (layout: ScreenLayout) => ScreenLayout,
): HmiScreen {
  const existing = screen.layouts.find((l) => l.target === target);
  const base: ScreenLayout = existing ?? { target, placements: {} };
  const next = fn(base);
  const layouts = existing
    ? screen.layouts.map((l) => (l.target === target ? next : l))
    : [...screen.layouts, next];
  return { ...screen, layouts };
}

/** Leeres Projekt mit einem ersten Screen. Ein Projekt ohne Screen waere
 *  eine Seite, auf der der Editor nichts anzeigen kann, und der Autor muesste
 *  als erstes eine Verwaltungsaufgabe loesen statt zu zeichnen. */
export function createEmptyProject(name: string, schemaVersion = '1.0'): HmiProject {
  return {
    id: '',
    name,
    schemaVersion,
    widgets: [],
    screens: [{ id: 'screen-1', label: 'Bild 1', widgetIds: [], layouts: [] }],
  };
}

/**
 * Widget anlegen und auf einem Screen platzieren.
 *
 * Die Platzierung gilt nur fuer das uebergebene Ziel. Die anderen Ziele
 * bekommen bewusst KEINE abgeleitete Platzierung: eine geratene Anordnung auf
 * einem Panel sieht aus wie eine gemachte, und der Autor wuerde sie nicht
 * mehr pruefen. Ohne Eintrag rendert das Ziel im Auto-Flow, und das ist als
 * unfertig erkennbar.
 */
export function addWidget(
  project: HmiProject,
  screenId: string,
  target: LayoutTarget,
  widget: WidgetDefinition,
  placement: WidgetPlacement,
): HmiProject {
  const withWidget: HmiProject = { ...project, widgets: [...project.widgets, widget] };
  return mapScreen(withWidget, screenId, (screen) =>
    upsertLayout(
      { ...screen, widgetIds: [...screen.widgetIds, widget.id] },
      target,
      (layout) => {
        // In einer freien Anordnung liegt ein neues Widget obenauf.
        const next = isFreeLayout(layout)
          ? normaliseFreePlacement(
            { ...placement, z: placement.z ?? topZ(layout.placements) + 1 }, target, frameOfLayout(layout))
          : clampPlacement(placement, target);
        return { ...layout, placements: { ...layout.placements, [widget.id]: next } };
      },
    ),
  );
}

/**
 * Widget vollstaendig entfernen: aus der Definitionsliste, aus jedem Screen
 * und aus jeder Platzierung jedes Ziels.
 *
 * Das Aufraeumen ueber ALLE Screens und Ziele ist der Punkt. Ein Placement,
 * das auf ein geloeschtes Widget zeigt, ist genau der Zustand, den die
 * serverseitige Validierung ablehnt, und er entstuende sonst beim Loeschen
 * auf einem Ziel, waehrend ein anderes noch den Eintrag haelt.
 */
export function removeWidget(project: HmiProject, widgetId: string): HmiProject {
  return {
    ...project,
    widgets: project.widgets.filter((w) => w.id !== widgetId),
    screens: project.screens.map((screen) => ({
      ...screen,
      widgetIds: screen.widgetIds.filter((id) => id !== widgetId),
      layouts: screen.layouts.map((layout) => {
        if (!(widgetId in layout.placements)) return layout;
        const placements = { ...layout.placements };
        delete placements[widgetId];
        return { ...layout, placements };
      }),
    })),
  };
}

/** Eigenschaften eines Widgets ersetzen. Die `id` bleibt, alles andere kommt
 *  vom Aufrufer: ein Eigenschaftseditor kennt seinen Typ, dieses Modul nicht. */
export function replaceWidget(project: HmiProject, next: WidgetDefinition): HmiProject {
  return {
    ...project,
    widgets: project.widgets.map((w) => (w.id === next.id ? next : w)),
  };
}

/**
 * Widget kopieren, inklusive Platzierung auf dem aktuellen Ziel.
 *
 * Die Kopie landet um eine Zeile versetzt, nicht deckungsgleich. Deckungsgleich
 * waere die Kopie unsichtbar, und der Autor klickte ein zweites Mal auf
 * Duplizieren, weil er glaubt, es sei nichts passiert.
 */
export function duplicateWidget(
  project: HmiProject,
  screenId: string,
  target: LayoutTarget,
  widgetId: string,
): HmiProject {
  const source = findWidget(project, widgetId);
  if (!source) return project;

  const copy = { ...source, id: freshId(source.kind, widgetIds(project)) } as WidgetDefinition;
  if (copy.label) copy.label = `${copy.label} (Kopie)`;

  const existing = placementsOf(project, screenId, target)[widgetId];
  const layout = findScreen(project, screenId)?.layouts.find((l) => l.target === target);
  let placement: WidgetPlacement;
  if (layout && isFreeLayout(layout)) {
    // Frei: um 16 px nach rechts unten versetzt und obenauf, damit die Kopie
    // sichtbar neben dem Original liegt. Eine Sperre erbt sie nicht, sonst
    // liesse sie sich nicht von der Stelle ruecken.
    const base = existing ?? { col: 0, row: 0, colSpan: 3, rowSpan: 2, x: 16, y: 16, w: 240, h: 160 };
    placement = {
      ...base,
      x: (base.x ?? 0) + DUPLICATE_OFFSET,
      y: (base.y ?? 0) + DUPLICATE_OFFSET,
      z: topZ(layout.placements) + 1,
      locked: undefined,
    };
  } else {
    placement = existing
      ? { ...existing, row: existing.row + existing.rowSpan }
      : findFreeSlot(project, screenId, target, 3, 2);
  }

  return addWidget(project, screenId, target, copy, placement);
}

/** Platzierung eines einzelnen Widgets setzen. */
export function setPlacement(
  project: HmiProject,
  screenId: string,
  target: LayoutTarget,
  widgetId: string,
  placement: WidgetPlacement,
): HmiProject {
  return mapScreen(project, screenId, (screen) =>
    upsertLayout(screen, target, (layout) => ({
      ...layout,
      placements: {
        ...layout.placements,
        [widgetId]: isFreeLayout(layout)
          ? normaliseFreePlacement(placement, target, frameOfLayout(layout))
          : clampPlacement(placement, target),
      },
    })),
  );
}

/**
 * Alle Platzierungen eines Screens und Ziels auf einmal setzen.
 *
 * Das ist der Weg, den die Rasterflaeche nimmt: Gridstack meldet nach jedem
 * Ziehen den kompletten Stand, weil ein Zug auch NACHBARN verschiebt. Wuerde
 * der Editor nur das gezogene Widget uebernehmen, waeren die verdraengten
 * Nachbarn auf dem Bildschirm verschoben und im gespeicherten Projekt nicht.
 * Unbekannte Widget-Ids werden dabei verworfen statt uebernommen, sonst
 * schriebe ein Fehler in der Rasterflaeche ein verwaistes Placement ins
 * Projekt.
 */
export function setPlacements(
  project: HmiProject,
  screenId: string,
  target: LayoutTarget,
  placements: Readonly<Record<string, WidgetPlacement>>,
): HmiProject {
  const screen = findScreen(project, screenId);
  if (!screen) return project;
  const onScreen = new Set(screen.widgetIds);
  const layout = screen.layouts.find((l) => l.target === target);
  const free = layout !== undefined && isFreeLayout(layout);
  const frame = layout ? frameOfLayout(layout) : FRAME_SIZES[target];
  const previous = layout?.placements ?? {};

  const cleaned: Record<string, WidgetPlacement> = {};
  for (const [id, placement] of Object.entries(placements)) {
    if (!onScreen.has(id)) continue;
    // Frei: Pixel sind die Wahrheit, die Zellen werden daraus nachgefuehrt.
    // Raster: die Rasterflaeche meldet nur Zellen (toPlacements); was der
    // bisherige Eintrag sonst noch traegt (Pixel, Ebene), bleibt stehen.
    cleaned[id] = free
      ? normaliseFreePlacement(placement, target, frame)
      : clampPlacement({ ...(previous[id] ?? {}), ...placement }, target);
  }

  return mapScreen(project, screenId, (s) =>
    upsertLayout(s, target, (layout) => ({ ...layout, placements: cleaned })),
  );
}

/**
 * Platzierung in das Raster des Ziels zwingen.
 *
 * Spalten immer, Zeilen nur dort, wo das Ziel eine feste Hoehe hat. Auf einem
 * Panel ohne Scrollbalken ist eine Zeile jenseits der letzten kein Randfall,
 * sondern ein unsichtbares Bedienelement.
 */
export function clampPlacement(placement: WidgetPlacement, target: LayoutTarget): WidgetPlacement {
  const spec = GRID_SPECS[target];

  const colSpan = Math.max(1, Math.min(placement.colSpan, spec.columns));
  const col = Math.max(0, Math.min(placement.col, spec.columns - colSpan));

  let rowSpan = Math.max(1, placement.rowSpan);
  let row = Math.max(0, placement.row);
  if (spec.rows !== null) {
    rowSpan = Math.min(rowSpan, spec.rows);
    row = Math.min(row, spec.rows - rowSpan);
  }

  // Unbekannte Felder (Pixellage einer freien Anordnung, Ebene, Sperre)
  // bleiben stehen. Wer zwischen Raster und frei hin und her schaltet, soll
  // seine Pixel nicht beim ersten Rasterzug verlieren, siehe gridToFreePlacement.
  return { ...placement, col, row, colSpan, rowSpan };
}

/**
 * Freier Platz fuer ein neues Widget.
 *
 * Absichtlich einfach: von oben nach unten die erste Zeile suchen, in der die
 * gewuenschte Breite frei ist. Kein Packen, kein Optimieren. Der Autor
 * schiebt das Widget ohnehin dorthin, wo es hingehoert, und ein cleverer
 * Algorithmus, der es woanders hinlegt als erwartet, kostet mehr Zeit als er
 * spart.
 *
 * Auf einem vollen Panel gibt es keinen freien Platz. Dann kommt die letzte
 * moegliche Position zurueck, damit das Widget sichtbar entsteht und der
 * Autor sieht, dass es eng wird, statt dass das Anlegen still fehlschlaegt.
 */
export function findFreeSlot(
  project: HmiProject,
  screenId: string,
  target: LayoutTarget,
  colSpan: number,
  rowSpan: number,
): WidgetPlacement {
  const spec = GRID_SPECS[target];
  const width = Math.max(1, Math.min(colSpan, spec.columns));
  const height = Math.max(1, rowSpan);
  const taken = Object.values(placementsOf(project, screenId, target));

  const maxRow = spec.rows !== null ? spec.rows - height : rowBound(taken) + height + 1;

  for (let row = 0; row <= Math.max(0, maxRow); row += 1) {
    for (let col = 0; col + width <= spec.columns; col += 1) {
      const candidate: WidgetPlacement = { col, row, colSpan: width, rowSpan: height };
      if (!taken.some((other) => overlaps(candidate, other))) return candidate;
    }
  }

  return clampPlacement({ col: 0, row: Math.max(0, maxRow), colSpan: width, rowSpan: height }, target);
}

function rowBound(placements: readonly WidgetPlacement[]): number {
  return placements.reduce((max, p) => Math.max(max, p.row + p.rowSpan), 0);
}

/** Ueberschneiden sich zwei Platzierungen? Beruehrende Kanten zaehlen nicht. */
export function overlaps(a: WidgetPlacement, b: WidgetPlacement): boolean {
  return (
    a.col < b.col + b.colSpan &&
    b.col < a.col + a.colSpan &&
    a.row < b.row + b.rowSpan &&
    b.row < a.row + a.rowSpan
  );
}

// ------------------------------------------- Anordnung von Ziel zu Ziel

/**
 * Ergebnis einer Uebernahme von einem Layout-Ziel auf ein anderes.
 *
 * `mode` gehoert in die Oberflaeche und nicht nur ins Protokoll: eine
 * massstaeblich uebernommene Anordnung ist dieselbe Zeichnung in anderer
 * Breite, eine neu umgebrochene ist eine andere. Wer das nicht gesagt
 * bekommt, haelt das Zweite fuer das Erste und prueft es nicht nach.
 *
 * `overflow` sind die Widgets, die auf dem Zielraster keinen Platz mehr
 * finden. Sie bekommen KEINE Platzierung, statt in die letzte Zeile gequetscht
 * zu werden: uebereinanderliegende Widgets sehen aus wie eine Anordnung und
 * sind keine, waehrend ein unplatziertes Widget auf der Flaeche gestrichelt
 * dasteht und damit als offen erkennbar bleibt.
 */
export interface LayoutTransfer {
  placements: Record<string, WidgetPlacement>;
  overflow: string[];
  mode: 'scaled' | 'reflowed';
}

/**
 * Breite eines Widgets auf dem Zielraster.
 *
 * Verhaeltnisgetreu und nicht absolut: eine Drittelbreite soll eine
 * Drittelbreite bleiben, sonst waere eine Uebernahme auf das Handy eine
 * Reihe von Saeulen. Die Untergrenze von zwei Spalten beim Verengen ist
 * dieselbe wie in `autoFlowPlacements`, und aus demselben Grund: auf vier
 * Spalten ist ein Viertel zu schmal, als dass ein Messwert darin lesbar
 * stuende. Beim Verbreitern gibt es keine Untergrenze, dort ist eine
 * einzelne Spalte eine bewusste Entscheidung des Autors.
 */
function spanOnTarget(colSpan: number, factor: number, to: GridSpec): number {
  const floor = factor < 1 ? Math.min(2, to.columns) : 1;
  return Math.max(floor, Math.min(to.columns, Math.round(colSpan * factor)));
}

/** Lesereihenfolge: oben vor unten, links vor rechts, Id als letzter
 *  Entscheid, damit dasselbe Layout nicht je nach Schluesselreihenfolge des
 *  Objekts zwei Ergebnisse hat. */
function inReadingOrder(
  placements: Readonly<Record<string, WidgetPlacement>>,
): [string, WidgetPlacement][] {
  return Object.entries(placements).sort(
    (a, b) =>
      a[1].row - b[1].row ||
      a[1].col - b[1].col ||
      (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0),
  );
}

/**
 * Treue Uebernahme: dieselbe Zeichnung, Spalten verhaeltnisgetreu umgerechnet.
 *
 * Gibt `null` zurueck, sobald das Ergebnis nicht mehr taugt, also bei einer
 * Ueberlappung oder wenn etwas unter die letzte Zeile eines festen Panels
 * fiele. Beides ist beim Verengen der Normalfall und nicht der Ausnahmefall:
 * zwoelf Spalten auf vier gerechnet legt Nachbarn uebereinander, und ein
 * Desktop-Bild ist schnell laenger als sechs Panelzeilen.
 */
function scaleOntoTarget(
  entries: readonly [string, WidgetPlacement][],
  from: GridSpec,
  to: GridSpec,
): Record<string, WidgetPlacement> | null {
  const factor = to.columns / from.columns;
  const out: Record<string, WidgetPlacement> = {};
  const placed: WidgetPlacement[] = [];

  for (const [id, source] of entries) {
    const colSpan = spanOnTarget(source.colSpan, factor, to);
    const col = Math.max(0, Math.min(Math.round(source.col * factor), to.columns - colSpan));
    const rowSpan = Math.max(1, source.rowSpan);
    const row = Math.max(0, source.row);

    if (to.rows !== null && row + rowSpan > to.rows) return null;
    const next: WidgetPlacement = { col, row, colSpan, rowSpan };
    if (placed.some((other) => overlaps(next, other))) return null;

    placed.push(next);
    out[id] = next;
  }
  return out;
}

/**
 * Neu umbrechen in der Lesereihenfolge der Quelle.
 *
 * Der Weg fuer alles, was sich nicht treu uebernehmen laesst. Gearbeitet wird
 * in Baendern: von links nach rechts fuellen, am Spaltenende umbrechen, das
 * naechste Band beginnt unter dem hoechsten Widget des vorigen. Daraus folgt
 * die Zusage, auf die es hier ankommt: innerhalb eines Bandes sind die
 * Spaltenbereiche disjunkt, die Baender selbst sind disjunkte Zeilenbaender,
 * also gibt es KEINE Ueberlappung. Der Test haelt das fest.
 *
 * Warum umbrechen und nicht klemmen. Klemmen heisst, zwoelf Spalten auf vier
 * zu stauchen und alles, was danach nicht mehr passt, an den Rand zu schieben;
 * das Ergebnis sind Widgets uebereinander. Eine Anordnung mit Ueberlappungen
 * ist schlechter als gar keine: sie sieht nach Arbeit aus, ist als Stapel auf
 * dem Geraet aber unbedienbar, und der Autor muesste sie erst auseinander
 * nehmen, bevor er anfangen kann. Die Lesereihenfolge ist dabei das, was ein
 * Mensch als Zusammenhang gemeint hat, also das Beste, was ohne Raten uebrig
 * bleibt.
 */
function flowOntoTarget(
  entries: readonly [string, WidgetPlacement][],
  from: GridSpec,
  to: GridSpec,
): LayoutTransfer {
  const factor = to.columns / from.columns;
  const placements: Record<string, WidgetPlacement> = {};
  const overflow: string[] = [];

  let col = 0;
  let bandRow = 0;
  let bandHeight = 0;

  for (const [id, source] of entries) {
    const colSpan = spanOnTarget(source.colSpan, factor, to);
    const rowSpan = to.rows !== null
      ? Math.max(1, Math.min(source.rowSpan, to.rows))
      : Math.max(1, source.rowSpan);

    if (col > 0 && col + colSpan > to.columns) {
      bandRow += bandHeight;
      col = 0;
      bandHeight = 0;
    }

    // Auf einem festen Panel ist unterhalb der letzten Zeile nichts. Das
    // Widget bleibt unplatziert und wird gemeldet; die Spalte laeuft dabei
    // nicht weiter, damit ein flacheres Widget hinter diesem noch in das
    // Band passt.
    if (to.rows !== null && bandRow + rowSpan > to.rows) {
      overflow.push(id);
      continue;
    }

    placements[id] = { col, row: bandRow, colSpan, rowSpan };
    col += colSpan;
    bandHeight = Math.max(bandHeight, rowSpan);
  }

  return { placements, overflow, mode: 'reflowed' };
}

/**
 * Anordnung eines Ziels auf ein anderes umrechnen.
 *
 * Zwei Wege, und der erste hat Vorrang: erst treu uebernehmen, und nur wenn
 * dabei etwas uebereinander oder aus dem Bild faellt, in der Lesereihenfolge
 * neu umbrechen. Der haeufigste Fall ist damit der beste: Desktop auf ein
 * Panel hat auf beiden Seiten zwoelf Spalten, die Zeichnung bleibt Zeile fuer
 * Zeile dieselbe, und die Luecken, die der Autor absichtlich gelassen hat,
 * bleiben Luecken. Ein Umbruch waere dort ein Schaden und kein Dienst.
 *
 * Das Ergebnis ist in beiden Faellen ein Ausgangspunkt und kein Endstand.
 * Deshalb landet es auch nicht von selbst im Projekt, siehe den Vorschlag im
 * `editorStore`.
 */
export function reflowPlacements(
  source: Readonly<Record<string, WidgetPlacement>>,
  from: LayoutTarget,
  to: LayoutTarget,
): LayoutTransfer {
  const fromSpec = GRID_SPECS[from];
  const toSpec = GRID_SPECS[to];
  const entries = inReadingOrder(source);
  if (entries.length === 0) return { placements: {}, overflow: [], mode: 'scaled' };

  const scaled = scaleOntoTarget(entries, fromSpec, toSpec);
  if (scaled) return { placements: scaled, overflow: [], mode: 'scaled' };

  return flowOntoTarget(entries, fromSpec, toSpec);
}

/**
 * Die Anordnung eines Bildes von einem Ziel auf ein anderes umrechnen.
 *
 * Uebernommen wird nur, was auf der Quelle auch WIRKLICH steht. Ein Widget,
 * das dort noch keine Platzierung hat, bekommt hier keine geraten; es bleibt
 * auf dem Ziel unplatziert und damit als offen sichtbar. Alles andere hiesse,
 * einen Vorschlag der Quelle (die gestrichelte Anfangsanordnung) als gemachte
 * Arbeit auf ein zweites Ziel zu kopieren.
 */
export function transferLayout(
  project: HmiProject,
  screenId: string,
  from: LayoutTarget,
  to: LayoutTarget,
): LayoutTransfer {
  const screen = findScreen(project, screenId);
  if (!screen) return { placements: {}, overflow: [], mode: 'scaled' };

  const onScreen = new Set(screen.widgetIds);
  const source: Record<string, WidgetPlacement> = {};
  for (const [id, placement] of Object.entries(placementsOf(project, screenId, from))) {
    if (onScreen.has(id)) source[id] = placement;
  }
  return reflowPlacements(source, from, to);
}

// ----------------------------------------------- Ausrichten und angleichen

/** Kante, an der ausgerichtet wird. */
export type AlignEdge = 'left' | 'right' | 'top' | 'bottom';

/**
 * Mehrere Widgets an einer Kante des ZUERST gewaehlten ausrichten.
 *
 * Das erste Element von `ids` ist der Bezug und bleibt, wo es ist. Warum das
 * erste und nicht etwa das linkeste: der Autor waehlt in einer Reihenfolge,
 * und seine erste Wahl ist die, die er schon fuer richtig haelt. Ein Bezug,
 * den die Funktion selbst bestimmt, ist bei jeder zweiten Anwendung ein
 * anderer, und dann richtet sich das Bild nach dem Zufall statt nach der
 * Absicht.
 *
 * Herein kommt die vollstaendige Anordnung des Ziels und heraus geht wieder
 * eine vollstaendige: die Flaeche schreibt immer den ganzen Stand (siehe
 * `setPlacements`), und ein Teilstand liesse die uebrigen Widgets als
 * unplatziert zurueck. Das Klemmen ins Raster passiert nicht hier, sondern in
 * `setPlacements`; zwei Klemmstellen waeren eine zu viel.
 */
export function alignPlacements(
  placements: Readonly<Record<string, WidgetPlacement>>,
  ids: readonly string[],
  edge: AlignEdge,
): Record<string, WidgetPlacement> {
  const out: Record<string, WidgetPlacement> = { ...placements };
  const anchor = ids.length > 0 ? out[ids[0]] : undefined;
  if (!anchor) return out;

  for (const id of ids.slice(1)) {
    const p = out[id];
    if (!p) continue;
    switch (edge) {
      case 'left':
        out[id] = { ...p, col: anchor.col };
        break;
      case 'right':
        out[id] = { ...p, col: Math.max(0, anchor.col + anchor.colSpan - p.colSpan) };
        break;
      case 'top':
        out[id] = { ...p, row: anchor.row };
        break;
      case 'bottom':
        out[id] = { ...p, row: Math.max(0, anchor.row + anchor.rowSpan - p.rowSpan) };
        break;
    }
  }
  return out;
}

/**
 * Mehrere Widgets auf die Groesse des ZUERST gewaehlten bringen.
 *
 * Getrennt nach Breite und Hoehe, weil beides getrennt gebraucht wird: eine
 * Reihe Kacheln nebeneinander soll gleich hoch sein und darf verschieden
 * breit bleiben. Die Ecke oben links bleibt liegen, gewachsen wird nach
 * rechts und nach unten, also in die Richtung, in die auch die Griffe ziehen.
 */
export function matchPlacementSize(
  placements: Readonly<Record<string, WidgetPlacement>>,
  ids: readonly string[],
  dimension: 'width' | 'height',
): Record<string, WidgetPlacement> {
  const out: Record<string, WidgetPlacement> = { ...placements };
  const anchor = ids.length > 0 ? out[ids[0]] : undefined;
  if (!anchor) return out;

  for (const id of ids.slice(1)) {
    const p = out[id];
    if (!p) continue;
    out[id] = dimension === 'width'
      ? { ...p, colSpan: anchor.colSpan }
      : { ...p, rowSpan: anchor.rowSpan };
  }
  return out;
}

// ------------------------------------------------------ Freie Anordnung
//
// docs/hmi-panel-ausbau.md §2. Je Bild und Ziel waehlt der Autor Raster oder
// frei. Frei heisst: x, y, w, h in Pixeln der Entwurfsflaeche, z als Ebene.
// Die Zellfelder bleiben im Datensatz und werden hier aus den Pixeln
// nachgefuehrt, damit aeltere Leser (und "Anordnung uebernehmen") eine
// brauchbare Naeherung finden statt lauter Nullen.
//
// Die Rechteckgeometrie (Einrasten, Griffe, Verteilen) steht in freeModel.ts.
// Was hier steht, braucht GRID_SPECS und FRAME_SIZES und deshalb diese Datei;
// ein Laufzeit-Import zwischen den beiden ginge am Testlaeufer vorbei (siehe
// validateProject), daher die kleine Klemme hier doppelt.

/** Versatz beim Duplizieren in der freien Anordnung, in px. */
const DUPLICATE_OFFSET = 16;
/** Kleinste Kante eines frei platzierten Widgets, in px. */
const MIN_FREE_PX = 8;

/** Steht dieses Layout auf freier Anordnung? Fehlt `mode`, ist es Raster. */
export function isFreeLayout(layout: ScreenLayout | null | undefined): boolean {
  return layout?.mode === 'free';
}

/** Der Layout-Eintrag eines Bildes fuer ein Ziel, oder null. */
export function layoutOf(project: HmiProject, screenId: string, target: LayoutTarget): ScreenLayout | null {
  return findScreen(project, screenId)?.layouts.find((l) => l.target === target) ?? null;
}

/** Entwurfsflaeche eines Layouts: die eigene, wenn sie taugt, sonst die
 *  Vorgabe des Ziels. */
export function frameOfLayout(layout: Pick<ScreenLayout, 'frame' | 'target'>): { width: number; height: number } {
  const f = layout.frame;
  if (f && Number.isFinite(f.width) && Number.isFinite(f.height) && f.width > 0 && f.height > 0) {
    return { width: Math.round(f.width), height: Math.round(f.height) };
  }
  return FRAME_SIZES[layout.target];
}

/** Hoechste Ebene in einer Anordnung, -1 ohne Eintraege. */
export function topZ(placements: Readonly<Record<string, WidgetPlacement>>): number {
  let max = -1;
  for (const p of Object.values(placements)) max = Math.max(max, p.z ?? 0);
  return max;
}

/**
 * Groesse einer Rasterzelle in Pixeln der Entwurfsflaeche.
 *
 * Breite: Rahmenbreite durch Spaltenzahl. Hoehe: auf Zielen mit fester
 * Zeilenzahl die Rahmenhoehe durch die Zeilen, damit ein volles Raster auch
 * den ganzen Rahmen fuellt (beim TP8 hoch 1280 / 12, nicht 12 x 100 = 1200
 * mit einem Streifen darunter); sonst die Zeilenhoehe des Rasters.
 */
export function freeCellSize(target: LayoutTarget, frame: { width: number; height: number }): { w: number; h: number } {
  const spec = GRID_SPECS[target];
  return {
    w: frame.width / spec.columns,
    h: spec.rows !== null ? frame.height / spec.rows : spec.cellHeightPx,
  };
}

/** Die Pixellage einer Platzierung, oder null, wenn sie keine vollstaendige hat. */
export function placementRect(p: WidgetPlacement): { x: number; y: number; w: number; h: number } | null {
  if ([p.x, p.y, p.w, p.h].some((v) => typeof v !== 'number' || !Number.isFinite(v))) return null;
  return { x: p.x!, y: p.y!, w: p.w!, h: p.h! };
}

function cellsOfRect(
  r: { x: number; y: number; w: number; h: number },
  target: LayoutTarget,
  frame: { width: number; height: number },
): Pick<WidgetPlacement, 'col' | 'row' | 'colSpan' | 'rowSpan'> {
  const cell = freeCellSize(target, frame);
  const clamped = clampPlacement(
    {
      col: Math.round(r.x / cell.w),
      row: Math.round(r.y / cell.h),
      colSpan: Math.max(1, Math.round(r.w / cell.w)),
      rowSpan: Math.max(1, Math.round(r.h / cell.h)),
    },
    target,
  );
  return { col: clamped.col, row: clamped.row, colSpan: clamped.colSpan, rowSpan: clamped.rowSpan };
}

function rectOfCells(
  p: Pick<WidgetPlacement, 'col' | 'row' | 'colSpan' | 'rowSpan'>,
  target: LayoutTarget,
  frame: { width: number; height: number },
): { x: number; y: number; w: number; h: number } {
  const cell = freeCellSize(target, frame);
  return {
    x: Math.round(p.col * cell.w),
    y: Math.round(p.row * cell.h),
    w: Math.round(Math.max(1, p.colSpan) * cell.w),
    h: Math.round(Math.max(1, p.rowSpan) * cell.h),
  };
}

/**
 * Eine freie Platzierung in Ordnung bringen: ganze Pixel, Mindestgroesse,
 * ganz im Rahmen, Zellen aus den Pixeln nachgefuehrt. Ebene und Sperre
 * bleiben, alles andere Unbekannte ebenfalls.
 */
export function normaliseFreePlacement(
  p: WidgetPlacement,
  target: LayoutTarget,
  frame: { width: number; height: number },
): WidgetPlacement {
  const raw = placementRect(p) ?? rectOfCells(p, target, frame);
  const w = Math.round(Math.max(MIN_FREE_PX, Math.min(raw.w, frame.width)));
  const h = Math.round(Math.max(MIN_FREE_PX, Math.min(raw.h, frame.height)));
  const x = Math.round(Math.max(0, Math.min(raw.x, frame.width - w)));
  const y = Math.round(Math.max(0, Math.min(raw.y, frame.height - h)));
  const out: WidgetPlacement = { ...p, ...cellsOfRect({ x, y, w, h }, target, frame), x, y, w, h };
  if (out.locked !== true) delete out.locked;
  if (typeof out.z !== 'number' || !Number.isFinite(out.z)) delete out.z;
  else out.z = Math.round(out.z);
  return out;
}

/**
 * Raster nach frei: x = col * Zellbreite und so weiter.
 *
 * Eine Ausnahme, damit Hin- und Zurueckschalten nichts kostet: traegt die
 * Platzierung noch Pixel aus einer frueheren freien Anordnung, und ergeben
 * diese Pixel genau die Zellen, die jetzt im Raster stehen, dann hat sie
 * seitdem niemand im Raster bewegt, und die genauen Pixel gelten wieder.
 */
export function gridToFreePlacement(
  p: WidgetPlacement,
  target: LayoutTarget,
  frame: { width: number; height: number },
): WidgetPlacement {
  const px = placementRect(p);
  if (px) {
    const cells = cellsOfRect(px, target, frame);
    if (cells.col === p.col && cells.row === p.row && cells.colSpan === p.colSpan && cells.rowSpan === p.rowSpan) {
      return normaliseFreePlacement(p, target, frame);
    }
  }
  return normaliseFreePlacement({ ...p, ...rectOfCells(p, target, frame) }, target, frame);
}

/** Frei nach Raster: die naechstgelegenen Zellen. Die Pixel bleiben im
 *  Datensatz stehen, siehe {@link gridToFreePlacement}. */
export function freeToGridPlacement(
  p: WidgetPlacement,
  target: LayoutTarget,
  frame: { width: number; height: number },
): WidgetPlacement {
  const px = placementRect(p);
  if (!px) return clampPlacement(p, target);
  return clampPlacement({ ...p, ...cellsOfRect(px, target, frame) }, target);
}

/**
 * Anordnung eines Bildes auf einem Ziel zwischen Raster und frei umschalten.
 *
 * Nach frei: jede Rasterplatzierung wird in Pixel umgerechnet, der Rahmen
 * bekommt die Vorgabe des Ziels (oder behaelt seinen eigenen), die Ebenen
 * folgen der Lesereihenfolge. Widgets ohne Platzierung auf diesem Ziel
 * bekommen eine Stelle im Fluss, damit nach dem Umschalten nichts fehlt.
 *
 * Nach Raster: naechstgelegene Zellen. Ueberlappungen raeumt die
 * Rasterflaeche beim ersten Anfassen selbst auf. Rahmen und Hintergrund
 * bleiben im Eintrag, damit ein Zurueckschalten sie wieder findet.
 */
export function setLayoutMode(
  project: HmiProject,
  screenId: string,
  target: LayoutTarget,
  mode: 'grid' | 'free',
): HmiProject {
  const screen = findScreen(project, screenId);
  if (!screen) return project;
  const current = screen.layouts.find((l) => l.target === target);
  if ((mode === 'free') === isFreeLayout(current)) return project;

  return mapScreen(project, screenId, (s) =>
    upsertLayout(s, target, (layout) => {
      const frame = frameOfLayout(layout);
      const onScreen = s.widgetIds;
      if (mode === 'grid') {
        const placements: Record<string, WidgetPlacement> = {};
        for (const id of onScreen) {
          const p = layout.placements[id];
          if (p) placements[id] = freeToGridPlacement(p, target, frame);
        }
        const rest: ScreenLayout = { ...layout, placements };
        delete rest.mode;
        return rest;
      }

      // Ebenen: eine fruehere freie Stapelfolge (z steht noch im Datensatz)
      // bleibt erhalten, sonst gilt die Lesereihenfolge.
      const placed = inReadingOrder(
        Object.fromEntries(onScreen.filter((id) => layout.placements[id]).map((id) => [id, layout.placements[id]])),
      ).map((entry, index) => ({ entry, index }))
        .sort((a, b) => (a.entry[1].z ?? 0) - (b.entry[1].z ?? 0) || a.index - b.index)
        .map((x) => x.entry);
      const placements: Record<string, WidgetPlacement> = {};
      placed.forEach(([id, p], index) => {
        placements[id] = { ...gridToFreePlacement(p, target, frame), z: index };
      });
      // Ohne Platzierung: im Raster unter das Bestehende fliessen lassen, dann
      // wie alle anderen umrechnen. So stehen sie ungefaehr dort, wo die
      // Rasterflaeche sie vorher gestrichelt gezeigt hat.
      const taken = placed.map(([, p]) => p);
      let z = placed.length;
      for (const id of onScreen) {
        if (layout.placements[id]) continue;
        const slot = findSlotAmong(taken, target, 3, 2);
        taken.push(slot);
        placements[id] = { ...gridToFreePlacement(slot, target, frame), z };
        z += 1;
      }
      return { ...layout, mode: 'free', frame: layout.frame ?? { ...FRAME_SIZES[target] }, placements };
    }),
  );
}

function findSlotAmong(
  taken: readonly WidgetPlacement[],
  target: LayoutTarget,
  colSpan: number,
  rowSpan: number,
): WidgetPlacement {
  const spec = GRID_SPECS[target];
  const width = Math.max(1, Math.min(colSpan, spec.columns));
  const height = spec.rows !== null ? Math.min(rowSpan, spec.rows) : rowSpan;
  const maxRow = spec.rows !== null ? spec.rows - height : rowBound(taken) + height + 1;
  for (let row = 0; row <= Math.max(0, maxRow); row += 1) {
    for (let col = 0; col + width <= spec.columns; col += 1) {
      const candidate: WidgetPlacement = { col, row, colSpan: width, rowSpan: height };
      if (!taken.some((other) => overlaps(candidate, other))) return candidate;
    }
  }
  return clampPlacement({ col: 0, row: Math.max(0, maxRow), colSpan: width, rowSpan: height }, target);
}

/** Hintergrund eines Layouts setzen oder (mit `undefined`) entfernen. Nur
 *  `#rrggbb` gilt als Farbe; alles andere faellt weg, statt in einem
 *  Stilattribut zu landen. */
export function setLayoutBackground(
  project: HmiProject,
  screenId: string,
  target: LayoutTarget,
  background: ScreenBackground | undefined,
): HmiProject {
  return mapScreen(project, screenId, (s) =>
    upsertLayout(s, target, (layout) => {
      const color = background?.color && /^#[0-9a-fA-F]{6}$/.test(background.color)
        ? background.color.toLowerCase()
        : undefined;
      const assetRef = background?.assetRef ? background.assetRef : undefined;
      const rest: ScreenLayout = { ...layout };
      delete rest.background;
      if (!color && !assetRef) return rest;
      return { ...rest, background: { ...(color ? { color } : {}), ...(assetRef ? { assetRef } : {}) } };
    }),
  );
}

/**
 * Die Anordnung eines anderen Ziels als freie Anordnung auf dieses Ziel
 * uebertragen: dieselbe Zeichnung, gleichmaessig verkleinert oder
 * vergroessert, so dass sie ganz in den neuen Rahmen passt, und darin
 * mittig. Eine Rasterquelle wird vorher in Pixel ihres eigenen Rahmens
 * umgerechnet. Nichts wird verzerrt: ein runder Knopf bleibt rund.
 *
 * Das Ergebnis ist ein Ausgangspunkt. Vom Querformat ins Hochformat bleibt
 * oben und unten Platz, und den soll der Autor sehen, statt dass die
 * Bausteine still in die Hoehe gezogen werden.
 */
export function transferFreeLayout(
  project: HmiProject,
  screenId: string,
  from: LayoutTarget,
  to: LayoutTarget,
): Record<string, WidgetPlacement> {
  const screen = findScreen(project, screenId);
  const source = screen?.layouts.find((l) => l.target === from);
  if (!screen || !source) return {};
  const fromFrame = frameOfLayout(source);
  const toLayout = screen.layouts.find((l) => l.target === to);
  const toFrame = toLayout ? frameOfLayout(toLayout) : FRAME_SIZES[to];
  const free = isFreeLayout(source);

  const rects: [string, { x: number; y: number; w: number; h: number }, WidgetPlacement][] = [];
  for (const id of screen.widgetIds) {
    const p = source.placements[id];
    if (!p) continue;
    const r = free ? placementRect(p) ?? rectOfCells(p, from, fromFrame) : rectOfCells(p, from, fromFrame);
    rects.push([id, r, p]);
  }
  if (rects.length === 0) return {};

  const scale = Math.min(toFrame.width / fromFrame.width, toFrame.height / fromFrame.height);
  const offX = (toFrame.width - fromFrame.width * scale) / 2;
  const offY = (toFrame.height - fromFrame.height * scale) / 2;
  const out: Record<string, WidgetPlacement> = {};
  rects.forEach(([id, r, p], index) => {
    out[id] = normaliseFreePlacement(
      {
        col: p.col, row: p.row, colSpan: p.colSpan, rowSpan: p.rowSpan,
        x: offX + r.x * scale, y: offY + r.y * scale, w: r.w * scale, h: r.h * scale,
        z: free ? p.z ?? index : index,
      },
      to,
      toFrame,
    );
  });
  return out;
}

/** Bausteine, die bedient werden. Fuer sie gilt auf Panels die
 *  Handschuh-Grenze, fuer reine Anzeigen nicht. */
export const COMMAND_WIDGET_KINDS: ReadonlySet<string> = new Set([
  'momentary-button', 'toggle', 'enum-setter', 'setpoint-stepper', 'slider', 'lock-guard',
  'action-button', 'nav-bar', 'theme-toggle', 'user-invite',
]);

/** Ziele, die ein fest verbautes Panel sind. */
const PANEL_TARGETS: ReadonlySet<LayoutTarget> = new Set<LayoutTarget>([
  'panelFixed1024x600', 'panelFixed1280x800', 'panelPortrait800x1280',
]);

// --------------------------------------------------------------- Screens

/** Neuen Screen anlegen. Die Id ist neu, das Label darf doppelt sein: der
 *  Autor darf zwei Bilder gleich nennen, die Maschine unterscheidet sie an
 *  der Id. */
export function addScreen(project: HmiProject, label: string): HmiProject {
  const id = freshId('screen', screenIds(project));
  return withScreens(project, [...project.screens, { id, label, widgetIds: [], layouts: [] }]);
}

/** Screen umbenennen. */
export function renameScreen(project: HmiProject, screenId: string, label: string): HmiProject {
  return mapScreen(project, screenId, (screen) => ({ ...screen, label }));
}

/**
 * Screen loeschen, samt der Widgets, die NUR auf ihm liegen.
 *
 * Ein Widget, das auf mehreren Screens erscheint, bleibt erhalten. Ein
 * Widget, das nirgends mehr erscheint, wuerde sonst als unerreichbare
 * Definition im Projekt liegen bleiben und bei der Datenpunkt-Zaehlung und in
 * der Lizenzsicht mitzaehlen, ohne dass irgendwo etwas davon zu sehen waere.
 */
export function removeScreen(project: HmiProject, screenId: string): HmiProject {
  const remaining = project.screens.filter((s) => s.id !== screenId);
  if (remaining.length === project.screens.length) return project;

  const stillReferenced = new Set(remaining.flatMap((s) => s.widgetIds));
  return {
    ...project,
    screens: remaining,
    widgets: project.widgets.filter((w) => stillReferenced.has(w.id)),
  };
}

/** Screen verschieben. `to` ist der Zielindex in der neuen Reihenfolge. */
export function moveScreen(project: HmiProject, screenId: string, to: number): HmiProject {
  const from = project.screens.findIndex((s) => s.id === screenId);
  if (from < 0) return project;
  const screens = [...project.screens];
  const [moved] = screens.splice(from, 1);
  screens.splice(Math.max(0, Math.min(to, screens.length)), 0, moved);
  return withScreens(project, screens);
}

/**
 * Ein Bild samt Widgets und allen vier Anordnungen kopieren.
 *
 * Der Anlass ist der zweite Heizkreis: er unterscheidet sich vom ersten in
 * den Bindungen und nicht im Aufbau. Kopiert werden deshalb die Widgets mit
 * allen Eigenschaften (auch den Bindungen, die der Autor danach umstellt) und
 * die Anordnung JEDES Ziels. Das Bild landet direkt hinter dem Original,
 * nicht am Ende: der zweite Heizkreis gehoert neben den ersten.
 *
 * <b>Die Ids muessen in den Platzierungen mitgezogen werden.</b> Jedes Widget
 * bekommt eine neue Id (zwei gleiche Ids waeren ein Fehler, den man erst am
 * verschwundenen Widget bemerkt), und jede kopierte Platzierung steht unter
 * der ALTEN Id, solange sie niemand umschluesselt. Herauskaeme ein Bild
 * voller Platzierungen fuer Widgets, die nicht darauf stehen, und genau das
 * lehnt der Server beim Speichern ab. Deshalb laeuft jede Id durch dieselbe
 * Abbildung, und was nicht darin steht, faellt weg statt verwaist zu bleiben.
 *
 * Ein Widget, das auch auf einem anderen Bild liegt, wird ebenfalls kopiert
 * und nicht geteilt. Geteilt waere es dasselbe Widget an zwei Stellen: wer
 * dann die Bindung des zweiten Heizkreises umstellt, stellt den ersten mit um.
 *
 * Zurueck kommt das Paar aus Projekt und neuer Bild-Id, damit der Aufrufer
 * ohne Raten dorthin schalten kann. Die Id ueber die Position zu suchen waere
 * moeglich, aber sie waere eine zweite Stelle, die die Einfuegeregel kennt.
 */
export function duplicateScreen(
  project: HmiProject,
  screenId: string,
): { project: HmiProject; screenId: string } {
  const index = project.screens.findIndex((s) => s.id === screenId);
  if (index < 0) return { project, screenId };
  const source = project.screens[index];

  // Die Menge waechst mit, sonst koennten sich zwei frische Ids innerhalb
  // desselben Durchlaufs treffen.
  const takenWidgets = widgetIds(project);
  const idMap = new Map<string, string>();
  const copies: WidgetDefinition[] = [];

  for (const oldId of source.widgetIds) {
    const widget = findWidget(project, oldId);
    if (!widget) continue;
    const newId = freshId(widget.kind, takenWidgets);
    takenWidgets.add(newId);
    idMap.set(oldId, newId);
    copies.push({ ...widget, id: newId } as WidgetDefinition);
  }

  const layouts: ScreenLayout[] = source.layouts.map((layout) => {
    const placements: Record<string, WidgetPlacement> = {};
    for (const [oldId, placement] of Object.entries(layout.placements)) {
      const newId = idMap.get(oldId);
      if (!newId) continue;
      placements[newId] = { ...placement };
    }
    return { ...layout, placements };
  });

  const copy: HmiScreen = {
    ...source,
    id: freshId('screen', screenIds(project)),
    label: `${source.label} (Kopie)`,
    widgetIds: source.widgetIds.map((id) => idMap.get(id)).filter((id): id is string => !!id),
    layouts,
  };

  const screens = [...project.screens];
  screens.splice(index + 1, 0, copy);
  return {
    project: { ...project, widgets: [...project.widgets, ...copies], screens },
    screenId: copy.id,
  };
}

// ------------------------------------------------------------ Validierung

/**
 * Kennung einer Befundart.
 *
 * Nicht Verzierung, sondern die Voraussetzung dafuer, dass die Oberflaeche
 * neben einem Befund etwas ANBIETEN kann. `message` ist fuer Menschen und
 * darf jederzeit umformuliert werden; wer daran eine Schaltflaeche festmacht,
 * verliert sie beim naechsten Federstrich. Beim Befund "liegt auf keinem
 * Bild" haengt genau das daran: er ist der einzige Ort, an dem ein
 * unerreichbares Widget ueberhaupt noch sichtbar ist, und ohne einen Weg zum
 * Aufraeumen bleibt er stehen, bis jemand das JSON von Hand anfasst.
 */
export type ValidationCode =
  | 'duplicate-widget-id'
  | 'duplicate-screen-id'
  | 'unknown-widget-ref'
  | 'orphan-placement'
  | 'out-of-columns'
  | 'below-last-row'
  | 'touch-target'
  | 'outside-frame'
  | 'free-without-position'
  | 'invalid-frame'
  | 'empty-screen'
  | 'widget-unplaced'
  | 'widget-unbound';

/** Ein Befund. `path` zeigt auf die Stelle im Projekt, `message` sagt einem
 *  Menschen, was zu tun ist, `code` sagt der Oberflaeche, was sie anbieten
 *  kann. */
export interface ValidationFinding {
  path: string;
  message: string;
  severity: 'error' | 'warning';
  code: ValidationCode;
  /**
   * Das betroffene Widget, wo es eines GIBT.
   *
   * Nur gesetzt, wenn die Id auch wirklich auf ein Widget in
   * `project.widgets` zeigt. Bei einem verwaisten Placement steht in `path`
   * ebenfalls eine Id, aber dahinter liegt nichts mehr; sie hier
   * mitzuschicken hiesse, der Oberflaeche eine Handhabe vorzugaukeln, die ins
   * Leere greift.
   */
  widgetId?: string;
}

/**
 * Projekt pruefen.
 *
 * Diese Pruefung spiegelt die serverseitige. Sie ersetzt sie nicht: der
 * Server prueft, weil er muss (ein Client ist kein Schutz), der Editor prueft,
 * weil ein Befund neben dem Speichern-Knopf hilfreicher ist als ein HTTP 400
 * nach dem Klick.
 *
 * Warnungen sind kein Fehler und blockieren das Speichern nicht. Ein Widget
 * ohne Bindung ist ein normaler Zwischenstand beim Zeichnen, und ein Editor,
 * der Zwischenstaende verbietet, zwingt den Autor dazu, seine Arbeit zu
 * verlieren, wenn er unterbrochen wird.
 *
 * <b>Warum `collectBindings` hereingereicht wird und nicht importiert.</b> Die
 * Funktion dafuer steht in `hmi/bindingKey.ts`, und ein Import von dort waere
 * der erste Laufzeit-Import dieses Moduls. Genau das wuerde die Eigenschaft
 * zerstoeren, wegen der die Regeln ueberhaupt hier liegen: der Testlaeufer
 * (`node --experimental-strip-types`) loest nur Pfade mit Endung auf, und die
 * TypeScript-Einstellung dieses Projekts erlaubt Endungen im Import nicht.
 * Dieselbe Ueberlegung steht schon in den Koepfen von `bindingKey.ts` und
 * `staleness.ts`, beide sind aus demselben Grund frei von Laufzeit-Importen.
 * Wird der Sammler nicht uebergeben, entfaellt nur die Bindungswarnung; alle
 * blockierenden Pruefungen laufen unabhaengig davon.
 */
export function validateProject(
  project: HmiProject,
  collectBindings?: (widget: WidgetDefinition) => readonly string[],
): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  const seenWidgets = new Set<string>();
  for (const widget of project.widgets) {
    if (seenWidgets.has(widget.id)) {
      findings.push({
        path: `widgets/${widget.id}`,
        message: `Die Widget-Id "${widget.id}" kommt mehrfach vor.`,
        severity: 'error',
        code: 'duplicate-widget-id',
        widgetId: widget.id,
      });
    }
    seenWidgets.add(widget.id);
  }

  const seenScreens = new Set<string>();
  for (const screen of project.screens) {
    if (seenScreens.has(screen.id)) {
      findings.push({
        path: `screens/${screen.id}`,
        message: `Die Screen-Id "${screen.id}" kommt mehrfach vor.`,
        severity: 'error',
        code: 'duplicate-screen-id',
      });
    }
    seenScreens.add(screen.id);

    for (const id of screen.widgetIds) {
      if (!seenWidgets.has(id)) {
        findings.push({
          path: `screens/${screen.id}/widgetIds`,
          message: `Das Bild "${screen.label}" verweist auf das unbekannte Widget "${id}".`,
          severity: 'error',
          code: 'unknown-widget-ref',
        });
      }
    }

    const onScreen = new Set(screen.widgetIds);
    for (const layout of screen.layouts) {
      const spec = GRID_SPECS[layout.target];
      if (isFreeLayout(layout)) {
        findings.push(...validateFreeLayout(project, screen, layout, onScreen));
        continue;
      }
      for (const [id, placement] of Object.entries(layout.placements)) {
        if (!onScreen.has(id)) {
          findings.push({
            path: `screens/${screen.id}/layouts/${layout.target}/placements/${id}`,
            message:
              `Im Bild "${screen.label}" liegt eine Platzierung für "${id}", ` +
              'aber das Widget steht nicht auf diesem Bild.',
            severity: 'error',
            code: 'orphan-placement',
          });
          continue;
        }
        if (spec && placement.col + placement.colSpan > spec.columns) {
          findings.push({
            path: `screens/${screen.id}/layouts/${layout.target}/placements/${id}`,
            message: `"${id}" ragt im Ziel ${spec.label} über die letzte Spalte hinaus.`,
            severity: 'error',
            code: 'out-of-columns',
          });
        }
        if (spec?.rows != null && placement.row + placement.rowSpan > spec.rows) {
          findings.push({
            path: `screens/${screen.id}/layouts/${layout.target}/placements/${id}`,
            message:
              `"${id}" liegt im Ziel ${spec.label} unterhalb der letzten Zeile ` +
              'und wäre auf dem Gerät nicht zu sehen.',
            severity: 'error',
            code: 'below-last-row',
          });
        }

        // Touch-Target-Pruefung aus J.8.
        //
        // Ehrlichkeit an dieser Stelle: mit dem heutigen Rastermass kann diese
        // Warnung NICHT ausloesen. Eine einzelne Zelle ist auf dem 1024er
        // bereits 85 auf 100 px gross, also ueber der Handschuh-Grenze von 64,
        // und kleiner als eine Zelle laesst sich nichts platzieren. Das ist
        // Absicht und die bessere Loesung: ein zu kleines Bedienelement ist auf
        // diesem Raster nicht ausdrueckbar, und eine Warnung, die man
        // wegklicken kann, waere schwaecher.
        //
        // Die Rechnung bleibt trotzdem stehen, als Waechter fuer den Tag, an
        // dem jemand das Raster feiner macht. Der Test
        // "das Rastermass garantiert die Touch-Grenze schon von selbst" haelt
        // die Zusage fest, diese Zeilen fangen den Fall danach ab.
        //
        // Warnung und nicht Fehler: eine reine Anzeige darf klein sein, nur
        // bedienen muss man sie nicht, und welche Widgets bedienbar sind,
        // weiss dieses Modul nicht.
        const touch = touchTargetSize(placement, layout.target);
        if (touch && !touch.ok) {
          findings.push({
            path: `screens/${screen.id}/layouts/${layout.target}/placements/${id}`,
            message:
              `"${id}" ist im Ziel ${spec.label} nur ${Math.round(touch.widthPx)} auf ` +
              `${Math.round(touch.heightPx)} px groß. Zum Bedienen mit Handschuh sind ` +
              `${MIN_TOUCH_TARGET_PX} px die Untergrenze.`,
            severity: 'warning',
            code: 'touch-target',
          });
        }
      }
    }

    if (screen.widgetIds.length === 0) {
      findings.push({
        path: `screens/${screen.id}`,
        message: `Das Bild "${screen.label}" ist leer.`,
        severity: 'warning',
        code: 'empty-screen',
      });
    }
  }

  const placed = new Set(
    project.screens.flatMap((s) => s.widgetIds),
  );
  for (const widget of project.widgets) {
    if (!placed.has(widget.id)) {
      findings.push({
        path: `widgets/${widget.id}`,
        message: `Das Widget "${widget.label ?? widget.id}" liegt auf keinem Bild.`,
        severity: 'warning',
        code: 'widget-unplaced',
        widgetId: widget.id,
      });
    }

    // Ein Widget ganz ohne Bindung zeigt im Betrieb nichts an, und das faellt
    // erst auf der Anlage auf. Warnung und nicht Fehler: ein frisch abgelegtes
    // Widget hat zurecht noch keine Bindung, und ein Editor, der
    // Zwischenstaende nicht speichern laesst, kostet Arbeit, sobald jemand
    // unterbrochen wird.
    //
    // Der Alarmmelder ist die eine echte Ausnahme: er bindet nicht an einen
    // Punkt, sondern zeigt die Alarme seines Bereichs (siehe
    // AlarmIndicatorWidget in widgets.ts). Ihn hier zu melden waere eine
    // Warnung, die nie verschwindet, und eine Warnung, die man wegsehen lernt,
    // entwertet die anderen.
    // Der Filter auf nichtleere Schluessel ist noetig, nicht kosmetisch:
    // "nicht gebunden" ist im gespeicherten Projekt der leere String (die
    // Pflichtfelder der Widget-Typen sind `string` und nicht optional), und
    // `collectBindingKeys` nimmt den leeren String als Binding mit. Ohne den
    // Filter waere ein Widget, an dem noch nichts eingestellt ist, hier
    // gebunden und in der Diagnose der produktiven Seite ein KAPUTTES Binding
    // statt ein fehlendes. Das gehoert in bindingKey.ts nachgezogen, siehe
    // Notiz in docs/status.md.
    const bound = collectBindings?.(widget).filter((key) => key.trim().length > 0) ?? null;
    if (bound !== null && widget.kind !== 'alarm-indicator' && bound.length === 0) {
      findings.push({
        path: `widgets/${widget.id}`,
        message:
          `Das Widget "${widget.label ?? widget.id}" ist an keinen Datenpunkt gebunden ` +
          'und bliebe auf der Anlage leer.',
        severity: 'warning',
        code: 'widget-unbound',
        widgetId: widget.id,
      });
    }
  }

  return findings;
}

/**
 * Befunde einer freien Anordnung.
 *
 * Bewusst fast nur Warnungen. Ueberlappen ist hier kein Fehler, sondern
 * Gestaltung (ein Wert auf einem Symbol, ein Knopf auf einer Flaeche). Ein
 * Baustein, der ueber den Rand ragt, ist auf dem Geraet abgeschnitten, aber
 * der Editor klemmt ohnehin in den Rahmen, so dass das nur bei einem von Hand
 * oder von aussen geschriebenen Projekt vorkommt; dann soll es auffallen und
 * das Speichern nicht verhindern. Fehler sind nur, was der Server ablehnt
 * oder was die Laufzeit nicht zeichnen kann: ein verwaistes Placement und
 * ein Rahmen ohne Flaeche.
 */
function validateFreeLayout(
  project: HmiProject,
  screen: HmiScreen,
  layout: ScreenLayout,
  onScreen: ReadonlySet<string>,
): ValidationFinding[] {
  const out: ValidationFinding[] = [];
  const spec = GRID_SPECS[layout.target];
  const base = `screens/${screen.id}/layouts/${layout.target}`;
  const f = layout.frame;
  if (f && !(Number.isFinite(f.width) && Number.isFinite(f.height) && f.width > 0 && f.height > 0)) {
    out.push({
      path: `${base}/frame`,
      message: `Die Entwurfsfläche von "${screen.label}" im Ziel ${spec.label} hat keine gültige Größe.`,
      severity: 'error',
      code: 'invalid-frame',
    });
  }
  const frame = frameOfLayout(layout);
  const kinds = new Map(project.widgets.map((w) => [w.id, w.kind] as const));

  for (const [id, placement] of Object.entries(layout.placements)) {
    const path = `${base}/placements/${id}`;
    if (!onScreen.has(id)) {
      out.push({
        path,
        message:
          `Im Bild "${screen.label}" liegt eine Platzierung für "${id}", ` +
          'aber das Widget steht nicht auf diesem Bild.',
        severity: 'error',
        code: 'orphan-placement',
      });
      continue;
    }
    const r = placementRect(placement);
    if (!r) {
      out.push({
        path,
        message: `"${id}" hat im Ziel ${spec.label} keine Pixellage und wird dort nicht gezeigt.`,
        severity: 'warning',
        code: 'free-without-position',
      });
      continue;
    }
    const outside = r.x + r.w <= 0 || r.y + r.h <= 0 || r.x >= frame.width || r.y >= frame.height;
    const partly = r.x < 0 || r.y < 0 || r.x + r.w > frame.width || r.y + r.h > frame.height;
    if (outside || partly) {
      out.push({
        path,
        message: outside
          ? `"${id}" liegt im Ziel ${spec.label} ganz außerhalb der Fläche und ist auf dem Gerät nicht zu sehen.`
          : `"${id}" ragt im Ziel ${spec.label} über den Rand und wird auf dem Gerät abgeschnitten.`,
        severity: 'warning',
        code: 'outside-frame',
      });
    }
    if (PANEL_TARGETS.has(layout.target) && COMMAND_WIDGET_KINDS.has(kinds.get(id) ?? '')
        && (r.w < MIN_TOUCH_TARGET_PX || r.h < MIN_TOUCH_TARGET_PX)) {
      out.push({
        path,
        message:
          `"${id}" ist im Ziel ${spec.label} nur ${Math.round(r.w)} auf ${Math.round(r.h)} px groß. ` +
          `Zum Bedienen mit Handschuh sind ${MIN_TOUCH_TARGET_PX} px die Untergrenze.`,
        severity: 'warning',
        code: 'touch-target',
        widgetId: id,
      });
    }
  }
  return out;
}

/** Nur die blockierenden Befunde. */
export function blockingFindings(findings: readonly ValidationFinding[]): ValidationFinding[] {
  return findings.filter((f) => f.severity === 'error');
}
